import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, } from 'react';
import { Base, Container, Flex, FlexGrid, FlexColumn, GlobalStyles, } from '@stepstone-genesis/components';
import { useActiveBreakpointValue } from '@stepstone-genesis/hooks';
import { useResizeObserver } from '@stepstone-genesis/resize-observer-library';
import { PageTemplateSidebarDrawer } from '@stepstone/atomic-org-page-template-sidebar-drawer';
import { getPageViewPayload, trackVariant } from 'tools/tracking';
import { AmaChatContent, AmaChatOpenProvider } from './AMAChat/AmaChatSection';
import { useHeaderOffset } from './AMAChat/hooks/useHeaderOffset';
import { useKeyboardOffset } from './AMAChat/hooks/useKeyboardOffset';
import { STICKY_FILL_HEIGHT_VAR, useStickyFillHeight, } from './AMAChat/hooks/useStickyFillHeight';
import { useStickyScrollportFix } from './AMAChat/hooks/useStickyScrollportFix';
import EditorContent from './EditorContent';
import ReportButton from './ReportButton/ReportButton';
export const App = (props) => {
    const decodedContent = !props.contentPayload && props.wordpressContent
        ? Buffer.from(props.wordpressContent, 'base64').toString('utf8')
        : '';
    const pageViewPayload = getPageViewPayload(props.companyId, props.storyId);
    const isMounted = useSyncExternalStore(() => () => undefined, () => true, () => false);
    const { companyId, numericCompanyId, companyName, storyId, amaTrackingVariant, reportUrl, } = props;
    const lang = props.lang === 'en' ? 'en' : 'de';
    // KIN-305 spec §0.5: populate `utag_data.abTestServerSide` with the resolved
    // variant id once per page-view, before any AMA event fires. trackVariant()
    // awaits the page-view event internally, so calling it on mount is safe.
    useEffect(() => {
        if (!amaTrackingVariant)
            return;
        trackVariant(amaTrackingVariant, 'askMeAnything');
    }, [amaTrackingVariant]);
    const headerOffset = useHeaderOffset();
    const isDesktop = useActiveBreakpointValue([false, false, true]);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const drawerRef = useRef(null);
    const { height: drawerHeight = 0 } = useResizeObserver({
        ref: drawerRef,
    });
    // Desktop: keep the sticky sidebar sized to the viewport space actually below
    // its current top edge. Publishes `STICKY_FILL_HEIGHT_VAR` on the wrapper
    // rather than returning a number, so scrolling does not re-render this tree —
    // see useStickyFillHeight for why the static calc() was wrong.
    const stickyWrapperRef = useRef(null);
    useStickyFillHeight(stickyWrapperRef, {
        enabled: !!isDesktop,
        stickyTopOffset: headerOffset,
    });
    // …and none of that matters unless `position: sticky` actually engages. The
    // stylesheet injected with WordPress story content turns html/body into dead
    // scroll containers, which silently disables it. See useStickyScrollportFix.
    useStickyScrollportFix(!!isDesktop);
    // iOS Safari does not push `position: fixed` ancestors above the on-screen
    // keyboard (verified in the iOS 26 Simulator: drawer stays anchored to the
    // pre-keyboard layout viewport). Android Chrome, on the other hand, lifts
    // fixed-positioned elements automatically when the package sets
    // `interactive-widget=resizes-content` — applying our manual lift there
    // would double-lift the drawer and scroll it off the top of the screen.
    // So we apply the workaround on iOS only.
    const isIOS = useMemo(() => {
        if (typeof navigator === 'undefined')
            return false;
        return (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
            // iPadOS 13+ identifies as MacIntel — distinguish via touch capability.
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
    }, []);
    const keyboardOffset = useKeyboardOffset();
    const liftedFixedAncestorRef = useRef(null);
    useEffect(() => {
        if (isDesktop || !isDrawerOpen || !isIOS)
            return;
        let el = drawerRef.current;
        while (el && getComputedStyle(el).position !== 'fixed') {
            el = el.parentElement;
        }
        if (!el)
            return;
        liftedFixedAncestorRef.current = el;
        // Two things at once on iOS:
        //   1) Lift the drawer above the keyboard via `bottom` (Framer Motion
        //      writes `transform`, so inline `transform` would be overwritten).
        //   2) Force min-height to the largest viewport height. The package's
        //      `openSize = Math.min(size, viewportHeight)` shrinks the drawer to
        //      `vv.height` when the keyboard opens, which would otherwise leave
        //      empty WordPress viewport area between the drawer and the keyboard.
        //      `100lvh` = large viewport height (without keyboard) keeps the
        //      drawer full-tall so it covers the visible area above the keyboard.
        el.style.transition = 'bottom 0.2s ease-out';
        el.style.bottom = keyboardOffset ? `${keyboardOffset}px` : '';
        el.style.minHeight = keyboardOffset ? '100lvh' : '';
    }, [keyboardOffset, isDesktop, isDrawerOpen, isIOS]);
    // Reset the lifted ancestor's inline styles when the drawer closes or this
    // component unmounts — but not on every offset change, so the property can
    // animate back to 0 instead of snapping.
    useEffect(() => {
        if (isDesktop || isDrawerOpen || !isIOS)
            return;
        const el = liftedFixedAncestorRef.current;
        if (!el)
            return;
        el.style.bottom = '';
        el.style.minHeight = '';
        el.style.transition = '';
        liftedFixedAncestorRef.current = null;
    }, [isDesktop, isDrawerOpen, isIOS]);
    // Lock body scroll while the mobile drawer is open. Genesis's SideBarBody
    // is configured non-modal (`modal: false`), so it does not lock scroll
    // itself — without this, swipes on the drawer can chain through to the
    // page underneath. Defence in depth is in Sidebar.tsx via
    // `overscroll-behavior: contain` on the chat scroll container.
    useEffect(() => {
        if (isDesktop || !isDrawerOpen)
            return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previous;
        };
    }, [isDesktop, isDrawerOpen]);
    // On desktop the drawer is the always-visible right sidebar — the package's
    // closeSize=0 on desktop would collapse it to 0px wide otherwise.
    const effectiveIsOpen = isDrawerOpen || !!isDesktop;
    // Tap-to-open for the closed peek strip — the package's Pully handle only
    // renders when isOpen=true. Read via ref so the handler stays stable across
    // open/close transitions and the drawer content does not remount.
    const isDrawerOpenRef = useRef(isDrawerOpen);
    isDrawerOpenRef.current = isDrawerOpen;
    const handlePeekClick = useCallback(() => {
        if (!isDrawerOpenRef.current)
            setIsDrawerOpen(true);
    }, []);
    // Deliberately omit `effectiveIsOpen` from the deps: a new function
    // identity here would make PageTemplateSidebarDrawer treat `content` as a
    // different component on every open/close, remounting AmaChatContent and
    // resetting the conversation. Open-state instead flows through
    // AmaChatOpenProvider further down the tree.
    const drawerContent = useMemo(() => () => (React.createElement(Flex, { onClick: handlePeekClick, sx: {
            flex: 1,
            minHeight: 0,
            flexDirection: 'column',
            // Desktop needs a *definite* height here, not just `flex: 1`:
            // PageTemplateSidebarDrawer's internal Motion/Base/Flex chain does
            // not reliably pass `display: flex` down to this node, so `flex: 1`
            // can be inert and the chat footer ends up clipped instead of the
            // conversation list scrolling internally. The custom property is
            // inherited from the sticky wrapper, which keeps it a single source
            // of truth and keeps this component free of dynamic values — no
            // extra `useMemo` dependency, so the chat still never remounts.
            // Mobile portals the drawer to document.body, where the property is
            // not defined, so the fallback applies and the height stays auto.
            height: `var(${STICKY_FILL_HEIGHT_VAR}, auto)`,
        } },
        React.createElement(AmaChatContent, { companyId: companyId, companyName: companyName, storyId: storyId }))), [companyId, companyName, storyId, handlePeekClick]);
    const reportButtonBlock = reportUrl && (React.createElement(Container, { custom: ['containerS', 'containerM', 'containerL', 'containerL'], sx: {
            flex: 1,
            minWidth: 0,
            paddingX: ['spaceM', 'spaceM', 'spaceXxl', 'spaceXxl'],
        } },
        React.createElement(Base, { sx: { display: 'flex', justifyContent: 'flex-end' } },
            React.createElement(ReportButton, { url: reportUrl, lang: lang, companyId: companyId }))));
    const pageContent = props.contentPayload ? (React.createElement("div", null,
        reportButtonBlock,
        React.createElement(EditorContent, { data: props.contentPayload, lang: lang, companyId: companyId, numericCompanyId: numericCompanyId, companyName: companyName, hasReportButtonAbove: Boolean(reportUrl) }))) : (React.createElement("div", null,
        reportButtonBlock,
        React.createElement(Base, { dangerouslySetInnerHTML: { __html: decodedContent }, suppressHydrationWarning: true })));
    if (props.isAMAEnabled && isMounted) {
        return (React.createElement(AmaChatOpenProvider, { value: effectiveIsOpen },
            React.createElement(GlobalStyles, { styles: {
                    // Mobile: leave room below the footer for the closed peek strip
                    // (the package portals the drawer to document.body with fixed
                    // positioning, so it would otherwise cover the page bottom).
                    // Desktop: drawer is on the right, no bottom padding needed.
                    body: {
                        paddingBottom: [`${drawerHeight}px`, `${drawerHeight}px`, 0],
                    },
                } }),
            React.createElement(Base, { sx: {
                    display: 'flex',
                    flexDirection: ['column', 'column', 'row'],
                    alignItems: [null, null, 'flex-start'],
                } },
                props.contentPayload ? (React.createElement(Base, { sx: { flex: 1, minWidth: 0 }, "data-atx-onpageview-payload": pageViewPayload },
                    React.createElement(Container, { custom: [
                            'containerS',
                            'containerM',
                            'containerL',
                            'containerL',
                        ] },
                        React.createElement(FlexGrid, { gutter: "spaceNone", marginTop: "spaceS" },
                            React.createElement(FlexColumn, { span: 12 }, pageContent))))) : (React.createElement(Container, { fluid: true, sx: {
                        flex: 1,
                        minWidth: 0,
                        paddingX: ['spaceM', 'spaceM', 'size120', 'size120'],
                    }, "data-atx-onpageview-payload": pageViewPayload }, pageContent)),
                React.createElement(Base, { ref: stickyWrapperRef, sx: isDesktop
                        ? {
                            position: 'sticky',
                            top: `${headerOffset}px`,
                            alignSelf: 'flex-start',
                            // A definite `height` (not just min/max) is required so the
                            // package's Framer Motion `height: 100%` animation on its
                            // inner sidebar container resolves to a real pixel value —
                            // otherwise the chat footer is clipped instead of the
                            // message list scrolling internally.
                            //
                            // useStickyFillHeight keeps the custom property in sync with
                            // the space below this element's *current* top edge. The
                            // calc() fallback is only a one-paint approximation — it
                            // assumes the element is already stuck, which is why it can
                            // clip the chat footer and must not be the steady-state
                            // value. It applies until the measuring effect first runs.
                            height: `var(${STICKY_FILL_HEIGHT_VAR}, calc(100dvh - ${headerOffset}px))`,
                            flex: '0 0 auto',
                            // Constrain the drawer's height so its inner scroll region
                            // activates instead of the chat content overflowing and
                            // pushing the page footer down.
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            borderLeftWidth: 'size01',
                            borderLeftStyle: 'solid',
                            borderLeftColor: 'colorBorderDefault',
                        }
                        : undefined },
                    React.createElement(PageTemplateSidebarDrawer, { isOpen: effectiveIsOpen, setOpen: setIsDrawerOpen, sidebarRef: drawerRef, content: drawerContent, sx: {
                            // Desktop: fill the constrained sticky height so the inner
                            // flex chain (Flex → Sidebar) gets a real height to work with
                            // and the conversation list becomes internally scrollable.
                            flex: ['initial', 'initial', 1],
                            minHeight: [null, null, 0],
                            // Mobile: lift above WordPress sections that create stacking
                            // contexts with positive z-indices (e.g. Highlights tiles
                            // overlap the bottom sheet otherwise). Desktop: 0 keeps the
                            // inline sidebar below the fixed site header (z-index ~100+).
                            zIndex: ['zIndex100', 'zIndex100', 'zIndex0'],
                        } })))));
    }
    return (React.createElement(Container, { custom: props.contentPayload
            ? ['containerS', 'containerM', 'containerL', 'containerL']
            : undefined, "data-atx-onpageview-payload": pageViewPayload },
        React.createElement(FlexGrid, { gutter: "spaceNone", marginTop: "spaceS" },
            React.createElement(FlexColumn, { span: 12 }, pageContent))));
};
export default App;
